import type { ConnectionId, ObjectId, Size, Transform, Vec2 } from '@/types';
import type { ResizeHandle } from './manipulation';

/**
 * Explicit interaction states. Every pointer event is reduced against this machine
 * so there is exactly one source of truth for "what is the user doing". The machine
 * is pure: inputs describe what happened, effects describe what the host should do.
 *
 *   idle ─hover─▶ hover ─down on object─▶ press ─moved ≥ threshold─▶ drag
 *      └─down on empty─▶ marquee     └─up before moving─▶ click (select) ─▶ idle
 *      └─middle / space─▶ pan        └─alt+down on object─▶ connect
 *      └─handle-down─▶ resize | rotate
 *   Escape from any gesture ─▶ idle + gesture-cancelled
 */
export type InteractionMode = InteractionState['mode'];

interface Gesture {
  readonly pointerId: number;
}

interface ManipulationStart {
  readonly transform: Transform;
  readonly size: Size;
}

export type InteractionState =
  | { readonly mode: 'idle' }
  | { readonly mode: 'hover'; readonly objectId: ObjectId }
  | {
      readonly mode: 'press';
      readonly objectIds: readonly ObjectId[];
      readonly hit: ObjectId;
      readonly startScreen: Vec2;
      readonly startWorld: Vec2;
      /** Pointer went down on an already-selected object; a click narrows to it, a drag moves all. */
      readonly onSelected: boolean;
      readonly pointerId: number;
    }
  | ({
      readonly mode: 'drag';
      readonly objectIds: readonly ObjectId[];
      readonly startWorld: Vec2;
      readonly currentWorld: Vec2;
    } & Gesture)
  | ({
      readonly mode: 'resize';
      readonly objectId: ObjectId;
      readonly handle: ResizeHandle;
      readonly start: ManipulationStart;
      readonly startWorld: Vec2;
    } & Gesture)
  | ({
      readonly mode: 'rotate';
      readonly objectId: ObjectId;
      readonly start: ManipulationStart;
      readonly startWorld: Vec2;
    } & Gesture)
  | ({
      readonly mode: 'marquee';
      readonly startWorld: Vec2;
      readonly currentWorld: Vec2;
      readonly additive: boolean;
    } & Gesture)
  | ({ readonly mode: 'pan'; readonly lastScreen: Vec2 } & Gesture)
  | ({
      readonly mode: 'connect';
      /** The fixed end of the rubber band. */
      readonly sourceId: ObjectId;
      readonly currentWorld: Vec2;
      readonly targetId: ObjectId | null;
      /** Set when dragging an existing connection's end to a new object. */
      readonly reconnect: Reconnect | null;
    } & Gesture);

export interface Reconnect {
  readonly connectionId: ConnectionId;
  readonly end: 'source' | 'target';
}

export type InteractionInput =
  | {
      readonly kind: 'pointer-down';
      readonly pointerId: number;
      readonly screen: Vec2;
      readonly world: Vec2;
      readonly hit: ObjectId | null;
      readonly hitLocked: boolean;
      readonly selected: readonly ObjectId[];
      readonly spaceHeld: boolean;
      readonly shiftHeld: boolean;
      readonly altHeld: boolean;
      readonly button: number;
    }
  | {
      readonly kind: 'handle-down';
      readonly pointerId: number;
      readonly world: Vec2;
      readonly objectId: ObjectId;
      readonly handle: ResizeHandle | 'rotate';
      readonly start: ManipulationStart;
    }
  | {
      readonly kind: 'reconnect-down';
      readonly pointerId: number;
      readonly world: Vec2;
      readonly connectionId: ConnectionId;
      readonly end: 'source' | 'target';
      /** Object at the end that stays put. */
      readonly fixedId: ObjectId;
    }
  | {
      readonly kind: 'pointer-move';
      readonly pointerId: number;
      readonly screen: Vec2;
      readonly world: Vec2;
      /** Object under the pointer; used for hover and connect targets. */
      readonly hit?: ObjectId | null;
      readonly shiftHeld?: boolean;
    }
  | { readonly kind: 'pointer-up'; readonly pointerId: number }
  | { readonly kind: 'pointer-leave' }
  | { readonly kind: 'cancel' };

/** Side effects the host should perform after a transition. Kept as data for testability. */
export type InteractionEffect =
  | { readonly kind: 'hover'; readonly objectId: ObjectId | null }
  | { readonly kind: 'pan'; readonly screenDelta: Vec2 }
  | { readonly kind: 'select'; readonly objectIds: readonly ObjectId[]; readonly additive: boolean }
  | { readonly kind: 'toggle-select'; readonly objectId: ObjectId }
  /** Total displacement since the drag began; the host applies snapping and diffs against what it already applied. */
  | { readonly kind: 'move-objects'; readonly objectIds: readonly ObjectId[]; readonly totalWorldDelta: Vec2 }
  | { readonly kind: 'drag-end'; readonly objectIds: readonly ObjectId[]; readonly totalWorldDelta: Vec2 }
  | { readonly kind: 'marquee-update'; readonly startWorld: Vec2; readonly currentWorld: Vec2 }
  | { readonly kind: 'marquee-end'; readonly startWorld: Vec2; readonly endWorld: Vec2; readonly additive: boolean }
  | {
      readonly kind: 'resize';
      readonly objectId: ObjectId;
      readonly handle: ResizeHandle;
      readonly start: ManipulationStart;
      readonly startWorld: Vec2;
      readonly currentWorld: Vec2;
      readonly keepAspect: boolean;
    }
  | {
      readonly kind: 'rotate';
      readonly objectId: ObjectId;
      readonly start: ManipulationStart;
      readonly startWorld: Vec2;
      readonly currentWorld: Vec2;
      readonly snap: boolean;
    }
  | { readonly kind: 'manipulation-end'; readonly objectId: ObjectId }
  | {
      readonly kind: 'connect-update';
      readonly sourceId: ObjectId;
      readonly currentWorld: Vec2;
      readonly targetId: ObjectId | null;
    }
  | {
      readonly kind: 'connect-end';
      readonly sourceId: ObjectId;
      readonly targetId: ObjectId | null;
      readonly reconnect: Reconnect | null;
    }
  | { readonly kind: 'gesture-cancelled'; readonly mode: InteractionMode };

export interface Transition {
  readonly state: InteractionState;
  readonly effects: readonly InteractionEffect[];
}

export const IDLE: InteractionState = { mode: 'idle' };

const MIDDLE_BUTTON = 1;
/** Screen pixels the pointer must travel before a press becomes a drag. */
export const DRAG_THRESHOLD_PX = 4;

const distance = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });

type GestureState = Exclude<InteractionState, { mode: 'idle' } | { mode: 'hover' }>;

/** True while a pointer gesture (anything but idle/hover) is in progress. */
export const isGesture = (state: InteractionState): state is GestureState =>
  state.mode !== 'idle' && state.mode !== 'hover';

export const reduceInteraction = (state: InteractionState, input: InteractionInput): Transition => {
  switch (input.kind) {
    case 'cancel':
      if (!isGesture(state))
        return { state: IDLE, effects: state.mode === 'hover' ? [{ kind: 'hover', objectId: null }] : [] };
      return { state: IDLE, effects: [{ kind: 'gesture-cancelled', mode: state.mode }] };

    case 'pointer-leave':
      return state.mode === 'hover'
        ? { state: IDLE, effects: [{ kind: 'hover', objectId: null }] }
        : { state, effects: [] };

    case 'handle-down': {
      if (isGesture(state)) return { state, effects: [] };
      const effects: InteractionEffect[] = state.mode === 'hover' ? [{ kind: 'hover', objectId: null }] : [];
      if (input.handle === 'rotate') {
        return {
          state: {
            mode: 'rotate',
            objectId: input.objectId,
            start: input.start,
            startWorld: input.world,
            pointerId: input.pointerId,
          },
          effects,
        };
      }
      return {
        state: {
          mode: 'resize',
          objectId: input.objectId,
          handle: input.handle,
          start: input.start,
          startWorld: input.world,
          pointerId: input.pointerId,
        },
        effects,
      };
    }

    case 'reconnect-down': {
      if (isGesture(state)) return { state, effects: [] };
      const effects: InteractionEffect[] = state.mode === 'hover' ? [{ kind: 'hover', objectId: null }] : [];
      return {
        state: {
          mode: 'connect',
          sourceId: input.fixedId,
          currentWorld: input.world,
          targetId: null,
          reconnect: { connectionId: input.connectionId, end: input.end },
          pointerId: input.pointerId,
        },
        effects: [
          ...effects,
          { kind: 'connect-update', sourceId: input.fixedId, currentWorld: input.world, targetId: null },
        ],
      };
    }

    case 'pointer-down': {
      if (isGesture(state)) return { state, effects: [] };
      const leaveHover: InteractionEffect[] = state.mode === 'hover' ? [{ kind: 'hover', objectId: null }] : [];
      if (input.button === MIDDLE_BUTTON || input.spaceHeld) {
        return { state: { mode: 'pan', lastScreen: input.screen, pointerId: input.pointerId }, effects: leaveHover };
      }
      if (input.hit) {
        if (input.shiftHeld) {
          // Shift-click toggles membership and never starts a drag.
          return { state: IDLE, effects: [...leaveHover, { kind: 'toggle-select', objectId: input.hit }] };
        }
        if (input.altHeld) {
          return {
            state: {
              mode: 'connect',
              sourceId: input.hit,
              currentWorld: input.world,
              targetId: null,
              reconnect: null,
              pointerId: input.pointerId,
            },
            effects: [
              ...leaveHover,
              { kind: 'connect-update', sourceId: input.hit, currentWorld: input.world, targetId: null },
            ],
          };
        }
        const onSelected = input.selected.includes(input.hit);
        const objectIds = onSelected ? input.selected : [input.hit];
        const effects: InteractionEffect[] = onSelected
          ? leaveHover
          : [...leaveHover, { kind: 'select', objectIds, additive: false }];
        if (input.hitLocked) return { state: IDLE, effects };
        return {
          state: {
            mode: 'press',
            objectIds,
            hit: input.hit,
            startScreen: input.screen,
            startWorld: input.world,
            onSelected,
            pointerId: input.pointerId,
          },
          effects,
        };
      }
      return {
        state: {
          mode: 'marquee',
          startWorld: input.world,
          currentWorld: input.world,
          additive: input.shiftHeld,
          pointerId: input.pointerId,
        },
        effects: input.shiftHeld ? leaveHover : [...leaveHover, { kind: 'select', objectIds: [], additive: false }],
      };
    }

    case 'pointer-move': {
      if (state.mode === 'idle' || state.mode === 'hover') {
        const hit = input.hit ?? null;
        const current = state.mode === 'hover' ? state.objectId : null;
        if (hit === current) return { state, effects: [] };
        return { state: hit ? { mode: 'hover', objectId: hit } : IDLE, effects: [{ kind: 'hover', objectId: hit }] };
      }
      if (state.pointerId !== input.pointerId) return { state, effects: [] };
      switch (state.mode) {
        case 'pan':
          return {
            state: { ...state, lastScreen: input.screen },
            effects: [{ kind: 'pan', screenDelta: sub(input.screen, state.lastScreen) }],
          };
        case 'press': {
          if (distance(input.screen, state.startScreen) < DRAG_THRESHOLD_PX) return { state, effects: [] };
          const next = {
            mode: 'drag' as const,
            objectIds: state.objectIds,
            startWorld: state.startWorld,
            currentWorld: input.world,
            pointerId: state.pointerId,
          };
          return {
            state: next,
            effects: [
              { kind: 'move-objects', objectIds: state.objectIds, totalWorldDelta: sub(input.world, state.startWorld) },
            ],
          };
        }
        case 'drag':
          return {
            state: { ...state, currentWorld: input.world },
            effects: [
              { kind: 'move-objects', objectIds: state.objectIds, totalWorldDelta: sub(input.world, state.startWorld) },
            ],
          };
        case 'marquee':
          return {
            state: { ...state, currentWorld: input.world },
            effects: [{ kind: 'marquee-update', startWorld: state.startWorld, currentWorld: input.world }],
          };
        case 'resize':
          return {
            state,
            effects: [
              {
                kind: 'resize',
                objectId: state.objectId,
                handle: state.handle,
                start: state.start,
                startWorld: state.startWorld,
                currentWorld: input.world,
                keepAspect: input.shiftHeld ?? false,
              },
            ],
          };
        case 'rotate':
          return {
            state,
            effects: [
              {
                kind: 'rotate',
                objectId: state.objectId,
                start: state.start,
                startWorld: state.startWorld,
                currentWorld: input.world,
                snap: input.shiftHeld ?? false,
              },
            ],
          };
        case 'connect': {
          const target = input.hit !== undefined && input.hit !== state.sourceId ? input.hit : null;
          return {
            state: { ...state, currentWorld: input.world, targetId: target },
            effects: [
              { kind: 'connect-update', sourceId: state.sourceId, currentWorld: input.world, targetId: target },
            ],
          };
        }
      }
      break;
    }

    case 'pointer-up': {
      if (!isGesture(state) || state.pointerId !== input.pointerId) return { state, effects: [] };
      switch (state.mode) {
        case 'press':
          // A click on an already-selected object narrows the selection to it.
          return {
            state: IDLE,
            effects:
              state.onSelected && state.objectIds.length > 1
                ? [{ kind: 'select', objectIds: [state.hit], additive: false }]
                : [],
          };
        case 'drag':
          return {
            state: IDLE,
            effects: [
              {
                kind: 'drag-end',
                objectIds: state.objectIds,
                totalWorldDelta: sub(state.currentWorld, state.startWorld),
              },
            ],
          };
        case 'marquee':
          return {
            state: IDLE,
            effects: [
              {
                kind: 'marquee-end',
                startWorld: state.startWorld,
                endWorld: state.currentWorld,
                additive: state.additive,
              },
            ],
          };
        case 'resize':
        case 'rotate':
          return { state: IDLE, effects: [{ kind: 'manipulation-end', objectId: state.objectId }] };
        case 'connect':
          return {
            state: IDLE,
            effects: [
              { kind: 'connect-end', sourceId: state.sourceId, targetId: state.targetId, reconnect: state.reconnect },
            ],
          };
        case 'pan':
          return { state: IDLE, effects: [] };
      }
    }
  }
  return { state, effects: [] };
};
